import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { LocationContextService } from '../services/location-context.service';

/**
 * Interceptor to add X-Location-ID header to all HTTP requests
 */
@Injectable()
export class LocationContextInterceptor implements HttpInterceptor {

  // Endpoints that should NOT include the X-Location-ID header.
  // Parent-side auth endpoints (login-parent, two-factor/*, password-reset) are
  // intentionally NOT listed: the backend uses the header to disambiguate
  // duplicate parents across locations and responds with 409 when it's missing
  // and the lookup is ambiguous.
  private readonly excludedEndpoints = [
    '/login',
    '/register',
    '/register-parent',
    '/auth/select-location',
    '/auth/change-location',
    '/auth/user-locations'
  ];

  private locationContext = inject(LocationContextService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if this endpoint should be excluded
    const shouldExclude = this.excludedEndpoints.some(endpoint =>
      request.url.includes(endpoint)
    );

    if (shouldExclude) {
      return next.handle(request);
    }

    if (this.locationContext.isAdminViewMode()) {
      return next.handle(request);
    }

    // Get current location ID
    const locationId = this.locationContext.getCurrentLocationId();

    // If location context is set, add the header
    if (locationId !== null) {
      const clonedRequest = request.clone({
        setHeaders: {
          'X-Location-ID': locationId.toString()
        }
      });
      return next.handle(clonedRequest);
    }

    // No location context, proceed without the header
    return next.handle(request);
  }
}
